'use client'
import { useMemo, useCallback, useEffect } from 'react'
import { useGetCurrencyDataQuery } from '@/src/api/rtkQuery/currencyApi'
import { useAppSelector, useAppDispatch } from '@/src/redux/store'
import { setCurrency } from '@/src/redux/slices/currencySlice'
import { mapApiCurrencyOptions } from '@/src/utils/currency'
import { currencyOptions } from '@/src/constants/header'

export interface UseHeaderCurrencyReturn {
    currencyOptions: Array<{ code: string; label: string; Flag: React.ComponentType<any>; symbol: string }>
    selectedCurrency: { code: string; symbol: string } | null
    isLoading: boolean
    isError: boolean
    onCurrencySelect: (value: { code: string; symbol: string }) => void
    syncSelectedCurrency: (setSelected: React.Dispatch<React.SetStateAction<any>>) => void
}

/** Hook for header currency state and selection logic */
export const useHeaderCurrency = (isMounted: boolean): UseHeaderCurrencyReturn => {
    const dispatch = useAppDispatch()
    const currency = useAppSelector(state => state.currency.selected)
    const { data: currencyData, isLoading, isError } = useGetCurrencyDataQuery()

    const apiCurrencyOptions = useMemo(() => mapApiCurrencyOptions(currencyData), [currencyData])

    const finalCurrencyOptions = useMemo(() => {
        return !isLoading && !isError && apiCurrencyOptions.length > 0
            ? apiCurrencyOptions
            : currencyOptions
    }, [isLoading, isError, apiCurrencyOptions])

    const onCurrencySelect = useCallback(
        (value: { code: string; symbol: string }) => {
            dispatch(setCurrency({ code: value.code, symbol: value.symbol }))
        },
        [dispatch]
    )

    const selectedCurrencyObject = useMemo(() => {
        if (!currency) return null
        const found = finalCurrencyOptions.find((opt: any) => opt.code === currency)
        return found ? { code: found.code, symbol: found.symbol } : null
    }, [currency, finalCurrencyOptions])

    const syncSelectedCurrency = useCallback(
        (setSelected: React.Dispatch<React.SetStateAction<any>>) => {
            if (!isMounted) return
            if (!isLoading && !isError && apiCurrencyOptions.length > 0) {
                const found = apiCurrencyOptions.find((opt: any) => opt.code === currency)
                if (found) {
                    setSelected((prev: any) => ({
                        ...prev,
                        currency: found
                    }))
                }
            }
        },
        [isMounted, currency, apiCurrencyOptions, isLoading, isError]
    )

    return {
        currencyOptions: finalCurrencyOptions,
        selectedCurrency: selectedCurrencyObject,
        isLoading,
        isError,
        onCurrencySelect,
        syncSelectedCurrency
    }
}
